/**
 * 튜닝 사례에 저장된 실행계획을 **조회할 때** DBMS_XPLAN 과 비슷한 표로 다시 그린다.
 *
 * 왜 필요한가 (2026-09-13 저장본 확인): 초안을 만들 때 모델이 실행계획을 한 줄 요약으로 옮겨 적어
 *   0 SELECT STATEMENT  A-Rows 101  A-Time 00:00:01.45  Buffers 4415
 *   3 TABLE ACCESS BY LOCAL INDEX ROWID BATCHED 이체거래명세 A-Rows 9156 Buffers 4415
 * 처럼 **열 머리글·표·들여쓰기가 모두 사라진** 채 저장돼 있었다. 원문과 비교해 읽을 수가 없다.
 *
 * - 이미 표(`| Id | Operation |`)로 저장된 것은 **손대지 않는다** — 원문이 가장 정확하다.
 * - 한 줄 요약 형태면 Id · Operation · Name · 지표(A-Rows 등)를 가려 표로 그리고, Predicate 줄
 *   (`4 filter(...)`)은 Predicate Information 절로 모은다.
 * - **계층(들여쓰기)은 저장본에 없다.** 연산 종류로 추정한다: 스캔은 잎, 조인은 자식 둘, 그 밖은 자식 하나.
 *   추정이라는 사실을 화면이 알린다(inferredDepth) — 원문 보기로 되돌릴 수 있다.
 * - 저장값은 바꾸지 않는다. 표시만 바꾼다.
 */

/** Operation 을 이루는 낱말 — 여기 없는 대문자 낱말(EMP 같은 객체명)은 Name 으로 본다 */
const OP_WORDS = new Set(`
  SELECT INSERT UPDATE DELETE MERGE STATEMENT INTO MULTI-TABLE
  SORT ORDER GROUP BY AGGREGATE UNIQUE NOSORT JOIN CREATE INDEX
  HASH NESTED LOOPS OUTER ANTI SEMI NA CARTESIAN RIGHT FULL BUFFERED SKEW
  TABLE ACCESS STORAGE SAMPLE ROWID BATCHED LOCAL GLOBAL CLUSTER BY
  RANGE SCAN SKIP FAST UNIQUE DESCENDING MIN/MAX FIRST ROW ROWS
  PARTITION ITERATOR ALL SINGLE LIST SUBQUERY INLIST KEY
  VIEW PUSHED PREDICATE COUNT STOPKEY FILTER WINDOW RANK PIVOT UNPIVOT
  UNION UNION-ALL CONCATENATION MINUS INTERSECTION INTERSECT
  BITMAP CONVERSION TO FROM ROWIDS AND OR
  PX COORDINATOR SEND RECEIVE BLOCK QC RANDOM BROADCAST ITERATOR
  COLLECTION PICKLER FETCH REMOTE SEQUENCE CONNECT WITH PUMP LOAD AS TEMP
  CURSOR DURATION MEMORY RESULT CACHE FAST DUAL MAT_VIEW REWRITE
  DOMAIN EXTERNAL XMLTABLE EVALUATION RECURSIVE UNION
`.trim().split(/\s+/));

const LEAF = /\bINDEX\b.*\bSCAN\b|\bTABLE ACCESS (STORAGE )?(FULL|SAMPLE)\b|\bMAT_VIEW (REWRITE )?ACCESS FULL\b|\bFAST DUAL\b|\bREMOTE\b|\bCOLLECTION ITERATOR\b|\bBITMAP INDEX\b|\bEXTERNAL TABLE ACCESS\b/;
const BINARY = /\b(NESTED LOOPS|HASH JOIN|MERGE JOIN|BITMAP AND|BITMAP OR|BITMAP MINUS|MINUS|INTERSECTION|UNION-ALL|UNION ALL|CONCATENATION)\b/;

/** DBMS_XPLAN 이 쓰는 열 이름 — 이 순서로 늘어놓는다 */
const METRIC_ORDER = ['Starts', 'E-Rows', 'A-Rows', 'Rows', 'Bytes', 'Cost', '%CPU', 'A-Time', 'Time', 'Buffers', 'Reads', 'Writes',
  'OMem', '1Mem', 'Used-Mem', 'Pstart', 'Pstop', 'TQ', 'IN-OUT'];
const METRIC_KEYS = new Set(METRIC_ORDER);

const NL = '\n';
const isTableRow = (line) => /^\s*\|/.test(line);

/** 고정폭 글꼴에서 한 칸/두 칸 — 한글·한자는 두 칸을 차지해야 열이 맞는다 */
export function cellWidth(s) {
  let w = 0;
  for (const ch of String(s)) {
    const c = ch.codePointAt(0);
    const wide = (c >= 0x1100 && c <= 0x115f) || (c >= 0x2e80 && c <= 0xa4cf) || (c >= 0xac00 && c <= 0xd7a3)
      || (c >= 0xf900 && c <= 0xfaff) || (c >= 0xfe30 && c <= 0xfe4f) || (c >= 0xff00 && c <= 0xff60) || (c >= 0xffe0 && c <= 0xffe6);
    w += wide ? 2 : 1;
  }
  return w;
}

const pad = (s, width, right) => {
  const gap = ' '.repeat(Math.max(0, width - cellWidth(s)));
  return right ? gap + s : s + gap;
};

function arity(operation) {
  if (LEAF.test(operation)) return 0;
  if (BINARY.test(operation)) return 2;
  return 1;
}

/** "3 TABLE ACCESS BY ... 이름 A-Rows 9156 Buffers 4415" → { id, star, operation, name, metrics } */
function parseRow(tokens) {
  const idTok = tokens[0];
  const star = idTok.startsWith('*');
  const id = Number(idTok.replace('*', ''));
  let i = 1;
  const ops = [];
  while (i < tokens.length && OP_WORDS.has(tokens[i]) && !METRIC_KEYS.has(tokens[i])) {
    ops.push(tokens[i]);
    i += 1;
  }
  if (!ops.length) return null;
  const nameParts = [];
  while (i < tokens.length && !METRIC_KEYS.has(tokens[i])) {
    nameParts.push(tokens[i]);
    i += 1;
  }
  const metrics = {};
  let key = null;
  for (; i < tokens.length; i += 1) {
    if (METRIC_KEYS.has(tokens[i])) {
      key = tokens[i];
      metrics[key] = '';
    } else if (key) {
      metrics[key] = metrics[key] ? `${metrics[key]} ${tokens[i]}` : tokens[i];
    }
  }
  return { id, star, operation: ops.join(' '), name: nameParts.join(' '), metrics };
}

/**
 * @returns {{ text: string, reformatted: boolean, inferredDepth: boolean }}
 */
export function formatPlanText(planText) {
  const original = String(planText || '');
  const keep = { text: original, reformatted: false, inferredDepth: false };
  const lines = original.replace(/\r\n?/g, NL).split(NL);
  if (lines.filter(isTableRow).length >= 2) return keep;   // 이미 표다 — 원문 그대로
  // **TKPROF·SQL 트레이스의 행 원본 형식**(`Rows  Row Source Operation` · `161250   NESTED LOOPS (cr=… pr=… time=…)`)도 원문 그대로다
  // (2026-10-24 신고). 줄이 숫자로 시작해 한 줄 요약으로 잘못 읽혔다 — 행 수를 Id 로, (cr=…) 를 Name 으로 표를 다시 그려 계층·수치가 엉켰다
  if (lines.some((l) => /\bRow Source Operation\b/i.test(l)) || lines.filter((l) => /\(cr=\d+/.test(l)).length >= 2) return keep;
  // 이미 **들여쓰기로 계층이 있는** 계획도 그대로 둔다 — 한 줄 요약은 번호 뒤 공백이 늘 한 칸이다(그래서 계층을 추정한다)
  const leads = new Set(lines.map((l) => /^\s*\*?\d+(\s+)\S/.exec(l)).filter(Boolean).map((m) => m[1].length));
  if (leads.size > 1) return keep;

  const rows = [];
  const predicates = [];
  const before = [];
  const after = [];
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const tokens = line.split(/\s+/);
    if (/^\*?\d+$/.test(tokens[0]) && tokens.length > 1) {
      const rest = line.slice(line.indexOf(tokens[1]));
      if (/^(access|filter)\s*\(/i.test(rest)) {
        predicates.push({ id: Number(tokens[0].replace('*', '')), text: rest });
        continue;
      }
      const row = parseRow(tokens);
      if (row) {
        rows.push(row);
        continue;
      }
    }
    (rows.length ? after : before).push(line);
  }
  if (!rows.length) return keep;

  // 계층 추정 — 조상 중 자식 자리가 남은 가장 가까운 것 아래로 붙인다
  const stack = [];
  for (const row of rows) {
    while (stack.length && stack[stack.length - 1].remaining <= 0) stack.pop();
    const parent = stack[stack.length - 1];
    row.depth = parent ? parent.depth + 1 : 0;
    if (parent) parent.remaining -= 1;
    stack.push({ depth: row.depth, remaining: arity(row.operation) });
  }

  const predicateIds = new Set(predicates.map((p) => p.id));
  const used = METRIC_ORDER.filter((k) => rows.some((r) => r.metrics[k] != null && r.metrics[k] !== ''));
  const cols = [
    { head: 'Id', right: true, cell: (r) => `${r.star || predicateIds.has(r.id) ? '*' : ' '}${r.id}` },
    { head: 'Operation', cell: (r) => `${' '.repeat(r.depth)}${r.operation}` },
    { head: 'Name', cell: (r) => r.name },
    ...used.map((k) => ({ head: k, right: true, cell: (r) => r.metrics[k] || '' })),
  ];
  const widths = cols.map((c) => Math.max(cellWidth(c.head), ...rows.map((r) => cellWidth(c.cell(r)))));
  const renderLine = (cells, rightFlags) => `|${cells.map((v, i) => ` ${pad(v, widths[i], rightFlags[i])} `).join('|')}|`;
  const header = renderLine(cols.map((c) => c.head), cols.map(() => false));
  const sep = '-'.repeat(cellWidth(header));

  const out = [...before];
  if (before.length) out.push('');
  out.push(sep, header, sep);
  rows.forEach((r) => out.push(renderLine(cols.map((c) => c.cell(r)), cols.map((c) => !!c.right))));
  out.push(sep);
  if (predicates.length) {
    const title = 'Predicate Information (identified by operation id):';
    out.push('', title, '-'.repeat(title.length));
    const idWidth = Math.max(...predicates.map((p) => String(p.id).length));
    predicates.forEach((p) => out.push(`   ${String(p.id).padStart(idWidth)} - ${p.text}`));
  }
  if (after.length) out.push('', ...after);
  return { text: out.join(NL), reformatted: true, inferredDepth: true };
}
